"use client";

import React, { useCallback, useEffect, useMemo, useState, startTransition } from "react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { ArrowLeft, MessageSquare } from "lucide-react";
import { ReviewComment } from "../../shared/ReviewComment";
import { getCompanyName } from "@/lib/textron-data-processor";
import { DocumentManager } from "../../planning/DocumentManager";
import { getFY_YEAR } from "@/utils/sharepoint-env";

interface OthExpSubstantiveProps {
  onBack?: () => void;
  setActiveSection?: (section: string) => void;
  procedureId?: string;
}

type SectionId = "IPE Testing" | "Analytical Procedures" | "Corporate Social Responsibility";
const SECTION_IDS: SectionId[] = ["IPE Testing", "Analytical Procedures", "Corporate Social Responsibility"];

interface SectionState {
  textResponse?: { html: string; plainText: string };
  documents?: Array<{ id: string; type: string; name: string; content?: string; url?: string }>;
}

type SubItem = {
  id: string;
  label: string;
  PY1?: string;
  PY2?: string;
  PY3?: string;
};

type CalcRow = {
  id: string;
  particulars: string;
  PY1?: string;
  PY2?: string;
  PY3?: string;
  sub_items?: SubItem[];
  calculation?: string;
};

const csrRows: CalcRow[] = [
  { id: "OthExp-6", particulars: "Profit Before Tax", PY1: "", PY2: "", PY3: "" },
  { id: "OthExp-7", particulars: "Add: bounties or subsidies received from the government not credited to profit & loss u/s 198(2)", PY1: "", PY2: "", PY3: "" },
  {
    id: "OthExp-8",
    particulars: "Less: Items u/s 198(3) as listed below which have been given credit in profit and loss account",
    sub_items: [
      { id: "OthExp-8-1", label: "Profits by way of premium on shares", PY1: "", PY2: "", PY3: "" },
      { id: "OthExp-8-2", label: "Profits on sale of forfeited shares by the company", PY1: "", PY2: "", PY3: "" },
      { id: "OthExp-8-3", label: "Profits of capital nature including sale of undertaking(s) or part thereof", PY1: "", PY2: "", PY3: "" },
      { id: "OthExp-8-4", label: "Profits from sale of immovable property or fixed assets of capital nature unless the business consists of buying and selling of such property or assets (only in excess of original cost)", PY1: "", PY2: "", PY3: "" },
      { id: "OthExp-8-5", label: "Any fair value changes in carrying amount of asset or liability", PY1: "", PY2: "", PY3: "" },
      { id: "OthExp-8-6", label: "Any amount representing unrealized gains, notional gains or revaluation of assets", PY1: "", PY2: "", PY3: "" },
    ],
    PY1: "",
    PY2: "",
    PY3: "",
  },
  {
    id: "OthExp-9",
    particulars: "Add: Sums that should not be deducted u/s 198(5)",
    sub_items: [
      { id: "OthExp-9-1", label: "Any compensation, damages or payments made voluntarily", PY1: "", PY2: "", PY3: "" },
      { id: "OthExp-9-2", label: "Loss of a capital nature including loss on sale of the undertaking(s) (only upto the cost of the asset)", PY1: "", PY2: "", PY3: "" },
      { id: "OthExp-9-3", label: "Any fair value changes in carrying amount of asset or liability", PY1: "", PY2: "", PY3: "" },
    ],
    PY1: "",
    PY2: "",
    PY3: "",
  },
  { id: "OthExp-10", particulars: "Profit u/s 198", PY1: "", PY2: "", PY3: "" },
  { id: "OthExp-11", particulars: "CSR Expense to be incurred", calculation: "" },
];

export default function OthExpSubstantive({ onBack, setActiveSection, procedureId }: OthExpSubstantiveProps) {
  const [companyName, setCompanyName] = useState<string>("");
  const [areaPrefix, setAreaPrefix] = useState<string>("OTEX"); // Default prefix
  const [rows, setRows] = useState<CalcRow[]>(csrRows);
  const [sectionStates, setSectionStates] = useState<Record<SectionId, SectionState>>({
    "IPE Testing": { documents: [] },
    "Analytical Procedures": { documents: [] },
    "Corporate Social Responsibility": { documents: [] },
  });
  const [inferenceQuestion, setInferenceQuestion] = useState<SectionState>({
    documents: [],
  });
  const [dirtySections, setDirtySections] = useState<Set<SectionId>>(() => new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hasExistingFile, setHasExistingFile] = useState(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `OTHER_EXPENSES_${normalizedSection}`;
  };

  const header = useMemo(() => ["Particulars", "PY1", "PY2", "PY3"], []);

  const markDirty = (id: SectionId) => {
    setDirtySections((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  // Helper function to calculate Profit u/s 198 for a given period
  const calculateProfit198 = useCallback((period: "PY1" | "PY2" | "PY3", currentRows: CalcRow[]): number => {
    // Get Profit Before Tax (OthExp-6)
    const profitBeforeTaxRow = currentRows.find(r => r.id === "OthExp-6");
    const profitBeforeTax = parseFloat(profitBeforeTaxRow?.[period] || "0") || 0;

    // Get Add items: OthExp-7 (bounties/subsidies)
    const addBountiesRow = currentRows.find(r => r.id === "OthExp-7");
    const addBounties = parseFloat(addBountiesRow?.[period] || "0") || 0;

    // Get Add items: OthExp-9 (Sums that should not be deducted) - sum of sub-items
    const addSumsRow = currentRows.find(r => r.id === "OthExp-9");
    let addSums = 0;
    if (addSumsRow?.sub_items) {
      addSums = addSumsRow.sub_items.reduce((sum, item) => {
        return sum + (parseFloat(item[period] || "0") || 0);
      }, 0);
    }

    // Get Less items: OthExp-8 (Items u/s 198(3)) - sum of sub-items
    const lessItemsRow = currentRows.find(r => r.id === "OthExp-8");
    let lessItems = 0;
    if (lessItemsRow?.sub_items) {
      lessItems = lessItemsRow.sub_items.reduce((sum, item) => {
        return sum + (parseFloat(item[period] || "0") || 0);
      }, 0);
    }

    // Calculate: Profit Before Tax + all add items - all less items
    return profitBeforeTax + addBounties + addSums - lessItems;
  }, []);

  const updateCell = (idx: number, key: keyof CalcRow, value: string) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: value };
      
      // Calculate and update Profit u/s 198 row
      const profit198Idx = next.findIndex(r => r.id === "OthExp-10");
      if (profit198Idx !== -1) {
        const py1Value = calculateProfit198("PY1", next);
        const py2Value = calculateProfit198("PY2", next);
        const py3Value = calculateProfit198("PY3", next);

        const py1Str = py1Value !== 0 ? py1Value.toFixed(2) : "";
        const py2Str = py2Value !== 0 ? py2Value.toFixed(2) : "";
        const py3Str = py3Value !== 0 ? py3Value.toFixed(2) : "";

        next[profit198Idx] = {
          ...next[profit198Idx],
          PY1: py1Str,
          PY2: py2Str,
          PY3: py3Str,
        };
      }

      // Calculate and update CSR Expense row (use calculated profit198 values)
      const csrExpenseIdx = next.findIndex(r => r.id === "OthExp-11");
      if (csrExpenseIdx !== -1) {
        // Get Profit u/s 198 for all 3 years (use calculated values from above)
        const profit198Row = next.find(r => r.id === "OthExp-10");
        const py1 = parseFloat(profit198Row?.PY1 || "0") || 0;
        const py2 = parseFloat(profit198Row?.PY2 || "0") || 0;
        const py3 = parseFloat(profit198Row?.PY3 || "0") || 0;

        // Calculate average
        const average = (py1 + py2 + py3) / 3;

        // Calculate 2% of average
        const csrValue = average * 0.02;
        const csrStr = csrValue !== 0 ? csrValue.toFixed(2) : "";

        next[csrExpenseIdx] = {
          ...next[csrExpenseIdx],
          calculation: csrStr,
        };
      }

      return next;
    });
    markDirty("Corporate Social Responsibility");
  };

  const updateSubItemCell = (rowIdx: number, subItemIdx: number, key: "PY1" | "PY2" | "PY3", value: string) => {
    setRows((prev) => {
      const next = [...prev];
      if (next[rowIdx].sub_items && next[rowIdx].sub_items![subItemIdx]) {
        next[rowIdx] = {
          ...next[rowIdx],
          sub_items: next[rowIdx].sub_items!.map((item, idx) =>
            idx === subItemIdx ? { ...item, [key]: value } : item
          ),
        };
      }

      // Calculate and update Profit u/s 198 row
      const profit198Idx = next.findIndex(r => r.id === "OthExp-10");
      if (profit198Idx !== -1) {
        const py1Value = calculateProfit198("PY1", next);
        const py2Value = calculateProfit198("PY2", next);
        const py3Value = calculateProfit198("PY3", next);

        const py1Str = py1Value !== 0 ? py1Value.toFixed(2) : "";
        const py2Str = py2Value !== 0 ? py2Value.toFixed(2) : "";
        const py3Str = py3Value !== 0 ? py3Value.toFixed(2) : "";

        next[profit198Idx] = {
          ...next[profit198Idx],
          PY1: py1Str,
          PY2: py2Str,
          PY3: py3Str,
        };
      }

      // Calculate and update CSR Expense row (use calculated profit198 values)
      const csrExpenseIdx = next.findIndex(r => r.id === "OthExp-11");
      if (csrExpenseIdx !== -1) {
        // Get Profit u/s 198 for all 3 years (use calculated values from above)
        const profit198Row = next.find(r => r.id === "OthExp-10");
        const py1 = parseFloat(profit198Row?.PY1 || "0") || 0;
        const py2 = parseFloat(profit198Row?.PY2 || "0") || 0;
        const py3 = parseFloat(profit198Row?.PY3 || "0") || 0;

        // Calculate average
        const average = (py1 + py2 + py3) / 3;

        // Calculate 2% of average
        const csrValue = average * 0.02;
        const csrStr = csrValue !== 0 ? csrValue.toFixed(2) : "";

        next[csrExpenseIdx] = {
          ...next[csrExpenseIdx],
          calculation: csrStr,
        };
      }

      return next;
    });
    markDirty("Corporate Social Responsibility");
  };

  // Load company name first
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn("[OthExpSubstantive] Failed to load company name, using fallback");
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load area prefix from Sections.json
  useEffect(() => {
    const loadAreaPrefix = async () => {
      try {
        if ((window as any).sharePointAPI?.downloadSectionsJson) {
          const result = await (window as any).sharePointAPI.downloadSectionsJson();
          if (result?.success && result?.data) {
            const sectionsData = result.data;
            // Look for "Other Expenses" in Execution section
            if (sectionsData.Execution && sectionsData.Execution["Other Expenses"]) {
              setAreaPrefix(sectionsData.Execution["Other Expenses"]);
            }
          }
        }
      } catch (error) {
        console.warn("[OthExpSubstantive] Failed to load area prefix from Sections.json, using default OTEX:", error);
      }
    };
    loadAreaPrefix();
  }, []);

  // Load Execution_<PREFIX>_Responses.json
  useEffect(() => {
    if (!companyName || !areaPrefix) return;

    const loadResponses = async () => {
      try {
        const fileName = `Execution_${areaPrefix}_Responses.json`;
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);

        if (result?.success && result.data && result.data.substantiveProcedures) {
          const data = result.data.substantiveProcedures as Record<string, any>;

          const nextStates: Record<SectionId, SectionState> = {
            "IPE Testing": { documents: [] },
            "Analytical Procedures": { documents: [] },
            "Corporate Social Responsibility": { documents: [] },
          };
          
          // Load inference question
          const inferenceData = data["OTEX-INFERENCE"];
          if (inferenceData) {
            const inferenceDocs = Array.isArray(inferenceData.documents)
              ? inferenceData.documents.map((d: any) => {
                  if (typeof d === "string") {
                    return { id: `doc_${d}`, type: "linked", name: d };
                  }
                  return {
                    id: d.id || `doc_${d.name || d.fileName}`,
                    type: d.type || "linked",
                    name: d.name || d.fileName || "",
                    content: d.content,
                    url: d.url,
                  };
                })
              : [];
            setInferenceQuestion({
              textResponse: inferenceData.textResponse || { html: "", plainText: "" },
              documents: inferenceDocs,
            });
          }

          SECTION_IDS.forEach((sid) => {
            const s = data[sid];
            if (!s) return;

            const docs = Array.isArray(s.documents)
              ? s.documents.map((d: any) => {
                  if (typeof d === "string") {
                    return { id: `doc_${d}`, type: "linked", name: d };
                  }
                  return {
                    id: d.id || `doc_${d.name || d.fileName}`,
                    type: d.type || "linked",
                    name: d.name || d.fileName || "",
                    content: d.content,
                    url: d.url,
                  };
                })
              : [];

            nextStates[sid] = {
              textResponse: s.textResponse || { html: "", plainText: "" },
              documents: docs,
            };
          });

          // Load CSR table if present
          const csrData = data["Corporate Social Responsibility"];
          if (csrData && csrData.table && csrData.table.rows && Array.isArray(csrData.table.rows)) {
            // Convert OTEX table format back to CalcRow format
            const loadedRows: CalcRow[] = csrData.table.rows.map((row: any) => {
              const calcRow: CalcRow = {
                id: row.key || "",
                particulars: row.label || "",
                calculation: row.calculation,
              };
              if (row.values) {
                calcRow.PY1 = row.values.PY1 || "";
                calcRow.PY2 = row.values.PY2 || "";
                calcRow.PY3 = row.values.PY3 || "";
              }
              // Handle sub-items - convert from array of strings or objects
              if (row.subItems || row.sub_items) {
                const subItems = row.subItems || row.sub_items;
                if (Array.isArray(subItems) && subItems.length > 0) {
                  // Check if first item is a string (old format) or object (new format)
                  if (typeof subItems[0] === 'string') {
                    // Old format: array of strings - convert to SubItem format
                    calcRow.sub_items = subItems.map((label: string, idx: number) => ({
                      id: `${row.key}-${idx + 1}`,
                      label,
                      PY1: "",
                      PY2: "",
                      PY3: "",
                    }));
                  } else {
                    // New format: array of objects with values
                    calcRow.sub_items = subItems.map((item: any) => ({
                      id: item.id || `${row.key}-${item.label || ''}`,
                      label: item.label || item,
                      PY1: item.PY1 || item.values?.PY1 || "",
                      PY2: item.PY2 || item.values?.PY2 || "",
                      PY3: item.PY3 || item.values?.PY3 || "",
                    }));
                  }
                }
              }
              return calcRow;
            });
            if (loadedRows.length > 0) {
              setRows((prev) => {
                const next = loadedRows;
                
                // Recalculate Profit u/s 198 and CSR Expense after loading
                const profit198Idx = next.findIndex(r => r.id === "OthExp-10");
                if (profit198Idx !== -1) {
                  const py1Value = calculateProfit198("PY1", next);
                  const py2Value = calculateProfit198("PY2", next);
                  const py3Value = calculateProfit198("PY3", next);

                  const py1Str = py1Value !== 0 ? py1Value.toFixed(2) : "";
                  const py2Str = py2Value !== 0 ? py2Value.toFixed(2) : "";
                  const py3Str = py3Value !== 0 ? py3Value.toFixed(2) : "";

                  next[profit198Idx] = {
                    ...next[profit198Idx],
                    PY1: py1Str,
                    PY2: py2Str,
                    PY3: py3Str,
                  };
                }

                // Calculate and update CSR Expense row
                const csrExpenseIdx = next.findIndex(r => r.id === "OthExp-11");
                if (csrExpenseIdx !== -1) {
                  const profit198Row = next.find(r => r.id === "OthExp-10");
                  const py1 = parseFloat(profit198Row?.PY1 || "0") || 0;
                  const py2 = parseFloat(profit198Row?.PY2 || "0") || 0;
                  const py3 = parseFloat(profit198Row?.PY3 || "0") || 0;

                  const average = (py1 + py2 + py3) / 3;
                  const csrValue = average * 0.02;
                  const csrStr = csrValue !== 0 ? csrValue.toFixed(2) : "";

                  next[csrExpenseIdx] = {
                    ...next[csrExpenseIdx],
                    calculation: csrStr,
                  };
                }

                return next;
              });
            }
          }

          setSectionStates(nextStates);
          setHasExistingFile(true);
        } else {
          setHasExistingFile(false);
        }
      } catch (error) {
        console.error(
          `[OthExpSubstantive] Failed to load Execution_${areaPrefix}_Responses.json from SharePoint:`,
          error
        );
        setHasExistingFile(false);
      }
    };

    loadResponses();
  }, [companyName, areaPrefix]);

  const handleDocumentsChange = (sectionId: SectionId, documents: any[]) => {
    setSectionStates((prev) => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        documents: documents,
      },
    }));
    markDirty(sectionId);
  };

  const handleTextResponseChange = (sectionId: SectionId, textResponse: { html: string; plainText: string }) => {
    setSectionStates((prev) => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        textResponse: textResponse,
      },
    }));
    markDirty(sectionId);
  };

  const handleSave = useCallback(async () => {
    if (!companyName || !areaPrefix) {
      console.warn("[OthExpSubstantive] Cannot save: company name or area prefix not available");
      return;
    }

    // Check if there's any content to save
    const itemIdMap: Record<SectionId, string> = {
      "IPE Testing": "IPE_Testing",
      "Analytical Procedures": "Analytical_Procedures",
      "Corporate Social Responsibility": "Corporate_Social_Responsibility",
    };
    const sectionsWithContent = new Set<SectionId>();
    SECTION_IDS.forEach((sid) => {
      const state = sectionStates[sid] || {};
      const itemId = itemIdMap[sid];
      const hasTextbox = localStorage.getItem(`textbox_${itemId}`);
      const hasDocuments = state.documents && state.documents.length > 0;
      const hasTextResponse = state.textResponse && (state.textResponse.html || state.textResponse.plainText);
      if (hasTextbox || hasDocuments || hasTextResponse) {
        sectionsWithContent.add(sid);
      }
    });

    // Also check CSR table
    const hasTableData = rows.some((r) => r.PY1 || r.PY2 || r.PY3 || r.calculation);
    if (hasTableData) {
      sectionsWithContent.add("Corporate Social Responsibility");
    }

    if (dirtySections.size === 0 && sectionsWithContent.size === 0) {
      setIsEditMode(false);
      return;
    }

    // Use dirty sections OR sections with content
    const sectionsToSave = new Set([...dirtySections, ...sectionsWithContent]);

    // Auto-save any pending textbox content before processing responses
    const documentManagers = document.querySelectorAll('[data-document-manager]');
    for (const manager of Array.from(documentManagers)) {
      const itemId = manager.getAttribute('data-item-id');
      if (itemId) {
        const autoSaveMethod = (window as any)[`autoSaveTextbox_${itemId}`];
        if (autoSaveMethod && typeof autoSaveMethod === 'function') {
          autoSaveMethod();
        }
      }
    }

    // Wait for auto-save to complete
    await new Promise(resolve => setTimeout(resolve, 1500));

    setIsSaving(true);
    setSaveError(null);

    try {
      const fileName = `Execution_${areaPrefix}_Responses.json`;
      
      // Load existing file first so we can merge per section
      let existingData: any = {};
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (result?.success && result.data) {
          existingData = result.data;
        }
      } catch (error) {
        console.warn(
          `[OthExpSubstantive] Failed to download existing ${fileName}, starting from empty`,
          error
        );
      }

      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      // Build payload for sections to save
      const payload: any = {
        substantiveProcedures: {} as Record<string, any>,
      };

      for (const sid of sectionsToSave) {
        const state = sectionStates[sid] || { documents: [] };
        
        // Map section ID to DocumentManager itemId
        const itemIdMap: Record<SectionId, string> = {
          "IPE Testing": "IPE_Testing",
          "Analytical Procedures": "Analytical_Procedures",
          "Corporate Social Responsibility": "Corporate_Social_Responsibility",
        };
        const itemId = itemIdMap[sid];
        const textboxFromLocalStorage = localStorage.getItem(`textbox_${itemId}`);
        const hasTextboxContent = textboxFromLocalStorage && textboxFromLocalStorage.trim().length > 0;

        const fileDocuments = (state.documents || []).filter((d: any) => d.type !== 'textbox');
        const documentNames = fileDocuments.map((d: any) => d.name || d);

        let textResponse = { html: "", plainText: "" };
        if (hasTextboxContent) {
          textResponse = {
            html: textboxFromLocalStorage,
            plainText: textboxFromLocalStorage.replace(/<[^>]*>/g, '')
          };
        } else if (state.textResponse) {
          textResponse = state.textResponse;
        }

        if (sid === "Corporate Social Responsibility") {
          // Convert CalcRow[] to OTEX table format
          const tableRows = rows.map((row) => {
            const otexRow: any = {
              key: row.id,
              label: row.particulars,
            };
            // Convert sub_items to new format with values
            if (row.sub_items && row.sub_items.length > 0) {
              otexRow.subItems = row.sub_items.map((subItem) => ({
                id: subItem.id,
                label: subItem.label,
                PY1: subItem.PY1 || "",
                PY2: subItem.PY2 || "",
                PY3: subItem.PY3 || "",
              }));
            }
            if (row.calculation !== undefined) {
              otexRow.calculation = row.calculation;
            }
            if (row.PY1 !== undefined || row.PY2 !== undefined || row.PY3 !== undefined) {
              otexRow.values = {
                PY1: row.PY1 || "",
                PY2: row.PY2 || "",
                PY3: row.PY3 || "",
              };
            }
            return otexRow;
          });

          payload.substantiveProcedures[sid] = {
            textResponse: textResponse,
            documents: documentNames,
            table: {
              columns: ["Particulars", "PY1", "PY2", "PY3"],
              rows: tableRows,
            },
            lastUpdated: new Date().toISOString(),
            companyName: companyName || "",
          };
        } else {
          // IPE Testing and Analytical Procedures
          payload.substantiveProcedures[sid] = {
            textResponse: textResponse,
            documents: documentNames,
            table: [],
            lastUpdated: new Date().toISOString(),
            companyName: companyName || "",
          };
        }
      }

      // Save inference question - query db.json for section field like CustomProcedure
      const inferenceItemId = "OTEX-INFERENCE";
      const sectionToFind = `Execution_${areaPrefix}_${inferenceItemId}`;
      let inferenceDocumentNames: string[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
          if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
            inferenceDocumentNames = dbResult.data
              .map((file: any) => file.name || '')
              .filter((name: string) => name && name.trim().length > 0);
          }
        }
      } catch (error) {
        console.warn(`Failed to query db.json for section ${sectionToFind}:`, error);
      }

      const inferenceTextboxFromLocalStorage = localStorage.getItem(`textbox_${inferenceItemId}`);
      const hasInferenceTextboxContent = inferenceTextboxFromLocalStorage && inferenceTextboxFromLocalStorage.trim().length > 0;
      
      let inferenceTextResponse = inferenceQuestion.textResponse || { html: "", plainText: "" };
      if (hasInferenceTextboxContent) {
        inferenceTextResponse = {
          html: inferenceTextboxFromLocalStorage,
          plainText: inferenceTextboxFromLocalStorage.replace(/<[^>]*>/g, '')
        };
      }

      payload.substantiveProcedures[inferenceItemId] = {
        textResponse: inferenceTextResponse,
        documents: inferenceDocumentNames,
        table: [],
        lastUpdated: new Date().toISOString(),
        companyName: companyName || "",
      };

      // Merge only modified sections into existingData.substantiveProcedures
      Object.entries(payload.substantiveProcedures).forEach(([sid, value]) => {
        existingData.substantiveProcedures[sid] = value;
      });

      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName: fileName,
        referenceValue: `Execution ${areaPrefix} Substantive Responses`,
        folderName: "juggernaut",
        fyYear: getFY_YEAR(),
        sectionList: [`Execution_${areaPrefix}_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        console.error(
          `[OthExpSubstantive] Failed to upload ${fileName}:`,
          uploadResult?.error
        );
        throw new Error(
          uploadResult?.error || `Failed to save ${fileName}`
        );
      }

      setHasExistingFile(true);
      // Clear dirty flags on successful save
      setDirtySections(new Set());
      setIsEditMode(false);
    } catch (error: any) {
      console.error(
        `[OthExpSubstantive] Error while saving Execution_${areaPrefix}_Responses.json:`,
        error
      );
      setSaveError(
        error?.message || "Failed to save Other Expenses Substantive responses to SharePoint"
      );
    } finally {
      setIsSaving(false);
    }
  }, [companyName, areaPrefix, dirtySections, sectionStates, rows, inferenceQuestion]);

  const saveButtonLabel = useMemo(() => {
    if (isSaving) return "Saving...";
    if (saveError) return "Save (Retry)";
    if (hasExistingFile) return "Save Changes";
    return "Save";
  }, [hasExistingFile, isSaving, saveError]);

  // Map procedureId to SectionId
  const getSectionId = (procedureId?: string): SectionId | null => {
    if (!procedureId) return null;
    const mapping: Record<string, SectionId> = {
      "ipe-testing": "IPE Testing",
      "analytical-procedures": "Analytical Procedures",
      "corporate-social-responsibility": "Corporate Social Responsibility",
    };
    return mapping[procedureId] || null;
  };

  const currentSectionId = getSectionId(procedureId);

  const handleBack = () => {
    if (setActiveSection) {
      setActiveSection("othexp-substantive");
    } else if (onBack) {
      onBack();
    }
  };

  // If no procedureId or invalid procedureId, show nothing (should be handled by routing)
  if (!procedureId || !currentSectionId) {
    return null;
  }

  const getTitle = (sectionId: SectionId): string => {
    const titles: Record<SectionId, string> = {
      "IPE Testing": "IPE Testing",
      "Analytical Procedures": "Analytical Procedures",
      "Corporate Social Responsibility": "Corporate Social Responsibility - Net Profit u/s 198",
    };
    return titles[sectionId];
  };

  const getDescription = (sectionId: SectionId): string => {
    const descriptions: Record<SectionId, string> = {
      "IPE Testing": "Integrity of Processing Environment Testing for Other Expenses",
      "Analytical Procedures": "Perform analytical procedures for Other Expenses",
      "Corporate Social Responsibility": "Corporate Social Responsibility calculation and documentation",
    };
    return descriptions[sectionId];
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">{getTitle(currentSectionId)}</h2>
          <p className="text-gray-400">{getDescription(currentSectionId)}</p>
        </div>
        <Button variant="outline" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>

      <Card className="border-white/10 bg-black/30 text-white">
        <CardHeader className="sticky top-0 z-10 bg-black/30 backdrop-blur">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Other Expenses Substantive Procedures</CardTitle>
            <div className="flex items-center gap-2">
              {!isEditMode ? (
                <Button
                  onClick={() => {
                    startTransition(() => {
                      setIsEditMode(true);
                    });
                  }}
                  variant="outline"
                  size="sm"
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Edit
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleSave}
                    disabled={isSaving}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {saveButtonLabel}
                  </Button>
                  <Button
                    onClick={() => setIsEditMode(false)}
                    variant="outline"
                    size="sm"
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    Stop Editing
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {currentSectionId === "IPE Testing" && (
            <div className="rounded-lg border border-white/10 bg-white/5 p-4">
              <div className="mb-4 text-white font-medium">IPE Testing</div>
              <DocumentManager
                sectionKey="otex-substantive"
                itemId="IPE_Testing"
                itemQuestion="IPE Testing"
                itemTextResponse={sectionStates["IPE Testing"].textResponse}
                itemDocuments={sectionStates["IPE Testing"].documents}
                onDocumentsChange={(docs) => handleDocumentsChange("IPE Testing", docs)}
                onTextResponseChange={(textResp) => handleTextResponseChange("IPE Testing", textResp)}
                disabled={!isEditMode}
              />
            </div>
          )}

          {currentSectionId === "Analytical Procedures" && (
            <div className="rounded-lg border border-white/10 bg-white/5 p-4">
              <div className="mb-4 text-white font-medium">Analytical Procedures</div>
              <DocumentManager
                sectionKey="otex-substantive"
                itemId="Analytical_Procedures"
                itemQuestion="Analytical Procedures"
                itemTextResponse={sectionStates["Analytical Procedures"].textResponse}
                itemDocuments={sectionStates["Analytical Procedures"].documents}
                onDocumentsChange={(docs) => handleDocumentsChange("Analytical Procedures", docs)}
                onTextResponseChange={(textResp) => handleTextResponseChange("Analytical Procedures", textResp)}
                disabled={!isEditMode}
              />
            </div>
          )}

          {currentSectionId === "Corporate Social Responsibility" && (
            <div className="rounded-lg border border-white/10 bg-white/5 p-4">
              <div className="mb-4 text-white">Corporate Social Responsibility - Net Profit u/s 198</div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm text-white/90">
                  <thead className="text-xs text-white/70">
                    <tr>
                      {header.map((h) => (
                        <th key={h} className="border-b border-white/10 px-3 py-2 font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, idx) => (
                      <React.Fragment key={r.id}>
                        {/* Main row - if it has sub_items, don't show input fields, just the label */}
                        <tr className="align-top">
                          <td className="border-b border-white/5 px-3 py-2">
                            <div className="whitespace-pre-wrap text-white font-medium">{r.particulars}</div>
                          </td>
                          {r.sub_items && r.sub_items.length > 0 ? (
                            // Row with sub-items: no input fields, just empty cells
                            <>
                              <td className="border-b border-white/5 px-3 py-2"></td>
                              <td className="border-b border-white/5 px-3 py-2"></td>
                              <td className="border-b border-white/5 px-3 py-2"></td>
                            </>
                          ) : ("PY1" in r || "PY2" in r || "PY3" in r) ? (
                            // Row without sub-items: show input fields (read-only for calculated rows)
                            <>
                              <td className="border-b border-white/5 px-3 py-2">
                                {"PY1" in r ? (
                                  <input
                                    type="number"
                                    value={r.PY1 || ""}
                                    onChange={(e) => updateCell(idx, "PY1", e.target.value)}
                                    disabled={!isEditMode || r.id === "OthExp-10"}
                                    readOnly={r.id === "OthExp-10"}
                                    className="w-full rounded border border-white/10 bg-black/40 p-2 text-white outline-none focus:border-white/30 disabled:opacity-50"
                                  />
                                ) : null}
                              </td>
                              <td className="border-b border-white/5 px-3 py-2">
                                {"PY2" in r ? (
                                  <input
                                    type="number"
                                    value={r.PY2 || ""}
                                    onChange={(e) => updateCell(idx, "PY2", e.target.value)}
                                    disabled={!isEditMode || r.id === "OthExp-10"}
                                    readOnly={r.id === "OthExp-10"}
                                    className="w-full rounded border border-white/10 bg-black/40 p-2 text-white outline-none focus:border-white/30 disabled:opacity-50"
                                  />
                                ) : null}
                              </td>
                              <td className="border-b border-white/5 px-3 py-2">
                                {"PY3" in r ? (
                                  <input
                                    type="number"
                                    value={r.PY3 || ""}
                                    onChange={(e) => updateCell(idx, "PY3", e.target.value)}
                                    disabled={!isEditMode || r.id === "OthExp-10"}
                                    readOnly={r.id === "OthExp-10"}
                                    className="w-full rounded border border-white/10 bg-black/40 p-2 text-white outline-none focus:border-white/30 disabled:opacity-50"
                                  />
                                ) : null}
                              </td>
                            </>
                          ) : (
                            // Calculation field (read-only for CSR Expense)
                            <td className="border-b border-white/5 px-3 py-2" colSpan={3}>
                              {"calculation" in r ? (
                                <input
                                  value={r.calculation || ""}
                                  onChange={(e) => updateCell(idx, "calculation", e.target.value)}
                                  disabled={!isEditMode || r.id === "OthExp-11"}
                                  readOnly={r.id === "OthExp-11"}
                                  placeholder="Enter computation or reference"
                                  className="w-full rounded border border-white/10 bg-black/40 p-2 text-white outline-none focus:border-white/30 disabled:opacity-50"
                                />
                              ) : null}
                            </td>
                          )}
                        </tr>
                        {/* Sub-items rows - appear as sublines with input fields */}
                        {r.sub_items && r.sub_items.length > 0 && r.sub_items.map((subItem, subIdx) => (
                          <tr key={subItem.id} className="align-top">
                            <td className="border-b border-white/5 px-3 py-2 pl-8">
                              <div className="text-white/80 text-sm whitespace-normal break-words">{subItem.label}</div>
                            </td>
                            <td className="border-b border-white/5 px-3 py-2">
                              <input
                                type="number"
                                value={subItem.PY1 || ""}
                                onChange={(e) => updateSubItemCell(idx, subIdx, "PY1", e.target.value)}
                                disabled={!isEditMode}
                                className="w-full rounded border border-white/10 bg-black/40 p-2 text-white outline-none focus:border-white/30 disabled:opacity-50"
                              />
                            </td>
                            <td className="border-b border-white/5 px-3 py-2">
                              <input
                                type="number"
                                value={subItem.PY2 || ""}
                                onChange={(e) => updateSubItemCell(idx, subIdx, "PY2", e.target.value)}
                                disabled={!isEditMode}
                                className="w-full rounded border border-white/10 bg-black/40 p-2 text-white outline-none focus:border-white/30 disabled:opacity-50"
                              />
                            </td>
                            <td className="border-b border-white/5 px-3 py-2">
                              <input
                                type="number"
                                value={subItem.PY3 || ""}
                                onChange={(e) => updateSubItemCell(idx, subIdx, "PY3", e.target.value)}
                                disabled={!isEditMode}
                                className="w-full rounded border border-white/10 bg-black/40 p-2 text-white outline-none focus:border-white/30 disabled:opacity-50"
                              />
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 text-xs text-white/70">
                Document our procedures to test entity's compliance with Section 135 of the Companies Act, 2013 read along with Companies (Corporate Social Responsibility Policy) Rules, 2014.
              </div>
              <div className="mt-4">
                <DocumentManager
                  sectionKey="otex-substantive"
                  itemId="Corporate_Social_Responsibility"
                  itemQuestion="Document our procedures to test entity's compliance with Section 135 of the Companies Act, 2013 read along with Companies (Corporate Social Responsibility Policy) Rules, 2014."
                  itemTextResponse={sectionStates["Corporate Social Responsibility"].textResponse}
                  itemDocuments={sectionStates["Corporate Social Responsibility"].documents}
                  onDocumentsChange={(docs) => handleDocumentsChange("Corporate Social Responsibility", docs)}
                  onTextResponseChange={(textResp) => handleTextResponseChange("Corporate Social Responsibility", textResp)}
                  disabled={!isEditMode}
                />
              </div>
            </div>
          )}
          
          {/* Inference and Conclusion Question */}
          <div className="mt-6 rounded-lg border border-white/10 bg-white/5 p-4">
            <div className="mb-4 text-white font-medium">Document the inference and conclusion from the above procedure performed.</div>
            <DocumentManager
              sectionKey="otex-substantive"
              itemId="OTEX-INFERENCE"
              itemQuestion="Document the inference and conclusion from the above procedure performed."
              itemTextResponse={inferenceQuestion.textResponse}
              itemDocuments={inferenceQuestion.documents}
              onDocumentsChange={(docs) => setInferenceQuestion({ ...inferenceQuestion, documents: docs })}
              onTextResponseChange={(textResp) => setInferenceQuestion({ ...inferenceQuestion, textResponse: textResp })}
              disabled={!isEditMode}
            />
          </div>
        </CardContent>
      </Card>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            // Map questionId back to section name for display
            const sectionMap: Record<string, string> = {
              [getSectionQuestionId("Other Expenses Substantive Procedures")]: "Other Expenses Substantive Procedures",
            };
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}
